import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Users, Wrench, FileText, MessageSquareQuote, LayoutDashboard } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import LeadsTab from "./LeadsTab";
import ServicesTab from "./ServicesTab";
import BlogsTab from "./BlogsTab";
import TestimonialsTab from "./TestimonialsTab";
import ContentTab from "./ContentTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="min-h-[60vh] bg-[#0B132B]" data-testid="admin-loading" />;

  return (
    <div className="bg-[#0B132B] min-h-screen" data-testid="admin-dashboard">
      <div className="border-b border-[#1E293B] bg-[#0f1830]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-5 h-5 text-[#F97316]" />
            <div>
              <h1 className="font-display text-xl font-bold text-white leading-none">Admin Panel</h1>
              <p className="text-[11px] text-slate-500 mt-0.5" data-testid="admin-user-email">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="text-xs text-slate-400 hover:text-[#F97316] transition-colors">
              View Site →
            </Link>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
              className="flex items-center gap-1.5 text-xs border border-[#1E293B] rounded-md px-3 py-2 text-slate-300 hover:text-red-400 hover:border-red-400/40 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="leads">
          <TabsList className="bg-[#131C38] border border-[#1E293B] flex-wrap h-auto gap-1 p-1" data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads" className="text-xs data-[state=active]:bg-[#F97316] data-[state=active]:text-[#0B132B]">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Leads
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services" className="text-xs data-[state=active]:bg-[#F97316] data-[state=active]:text-[#0B132B]">
              <Wrench className="w-3.5 h-3.5 mr-1.5" /> Services
            </TabsTrigger>
            <TabsTrigger value="blogs" data-testid="tab-blogs" className="text-xs data-[state=active]:bg-[#F97316] data-[state=active]:text-[#0B132B]">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Blog
            </TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="text-xs data-[state=active]:bg-[#F97316] data-[state=active]:text-[#0B132B]">
              <MessageSquareQuote className="w-3.5 h-3.5 mr-1.5" /> Testimonials
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="text-xs data-[state=active]:bg-[#F97316] data-[state=active]:text-[#0B132B]">
              <LayoutDashboard className="w-3.5 h-3.5 mr-1.5" /> Site Content
            </TabsTrigger>
          </TabsList>
          <TabsContent value="leads"><LeadsTab /></TabsContent>
          <TabsContent value="services"><ServicesTab /></TabsContent>
          <TabsContent value="blogs"><BlogsTab /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
          <TabsContent value="content"><ContentTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
